import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GripVertical, Plus, Settings2, Trash2 } from 'lucide-react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FilterTemplateValueToken } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pill } from '~/components/pill';
import { serverMessage } from '~/lib/api/server-error';
import { useFilterTemplateValues, useFilterTemplateValueMutations } from '~/hooks/use-filter-template-values';
import { reorderedRows, sortByStorefrontOrder } from '~/lib/schemas/filter-template-value';
import { cn } from '~/lib/utils';
import { AddFiltersDialog } from './add-filters-dialog';
import { FilterValueSettingsDialog } from './value-settings-dialog';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	templateId: number | null;
	templateName: string;
}

type ToggleField = 'IsExtended' | 'IsSingleSelect' | 'showInWizard' | 'isFeatured' | 'isImageFilter';

const TOGGLES: { field: ToggleField; labelKey: string }[] = [
	{ field: 'IsExtended', labelKey: 'catalog.filterTemplates.filterColumns.extended' },
	{ field: 'IsSingleSelect', labelKey: 'catalog.filterTemplates.filterColumns.singleSelect' },
	{ field: 'showInWizard', labelKey: 'catalog.filterTemplates.filterColumns.showInWizard' },
	{ field: 'isFeatured', labelKey: 'catalog.filterTemplates.filterColumns.featured' },
	{ field: 'isImageFilter', labelKey: 'catalog.filterTemplates.filterColumns.imageFilter' }
];

/** Table row whose handle drags it to a new storefront position (keyboard: focus the handle, Space, arrows, Space). */
function SortableValueRow({ id, dragLabel, children }: { id: number; dragLabel: string; children: ReactNode }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		position: 'relative',
		zIndex: isDragging ? 10 : undefined
	};

	return (
		<tr
			ref={setNodeRef}
			style={style}
			className={cn('border-b border-border-subtle bg-surface last:border-b-0 hover:bg-elevated', isDragging && 'opacity-80 shadow-md')}>
			<td className="w-8 py-2 pl-2">
				<button
					type="button"
					{...attributes}
					{...listeners}
					aria-label={dragLabel}
					title={dragLabel}
					className="flex h-6 w-6 cursor-grab touch-none items-center justify-center rounded-sm text-foreground-subtle hover:text-foreground active:cursor-grabbing">
					<GripVertical size={15} />
				</button>
			</td>
			{children}
		</tr>
	);
}

/** Side panel managing the filter set of a single template (legacy master-detail FilterTemplateValues grid). */
export function FilterTemplateValuesSheet({ open, onOpenChange, templateId, templateName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useFilterTemplateValues(open ? templateId : null);
	const { update, remove, reorder } = useFilterTemplateValueMutations(templateId);

	const [addOpen, setAddOpen] = useState(false);
	const [deleteRow, setDeleteRow] = useState<FilterTemplateValueToken | null>(null);
	const [settingsRow, setSettingsRow] = useState<FilterTemplateValueToken | null>(null);

	// The row order is the storefront order: ApplyFilterTemplate renders the template's filters by `si`.
	const rows = sortByStorefrontOrder(data ?? []);
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over) return;
		const changed = reorderedRows(rows, Number(active.id), Number(over.id));
		if (changed.length === 0) return;
		reorder.mutate(changed, { onError: (err: unknown) => toast.error(serverMessage(err)) });
	};

	const setFlag = (row: FilterTemplateValueToken, field: ToggleField, next: boolean) => {
		update.mutate({ ...row, [field]: next }, { onError: (err: unknown) => toast.error(serverMessage(err)) });
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
					<SheetHeader className="border-b border-border px-5 py-4">
						<SheetTitle>{t('catalog.filterTemplates.filtersTitle')}</SheetTitle>
						<SheetDescription>{templateName}</SheetDescription>
					</SheetHeader>

					<div className="flex items-center justify-between px-5 py-3">
						<div className="flex flex-col">
							<span className="text-xs text-foreground-muted">{t('catalog.filterTemplates.filterCount', { count: rows.length })}</span>
							<span className="text-[11px] text-foreground-subtle">{t('catalog.filterTemplates.orderHint')}</span>
						</div>
						<Button size="sm" disabled={!templateId} onClick={() => setAddOpen(true)}>
							<Plus size={15} /> {t('catalog.filterTemplates.addFilters')}
						</Button>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
						{isLoading ? (
							<Skeleton className="h-64 w-full rounded-md" />
						) : isError || rows.length === 0 ? (
							<div className="py-12 text-center text-sm text-foreground-subtle">{t('common.noResults')}</div>
						) : (
							<div className="overflow-x-auto rounded-md border border-border">
								<table className="w-full border-collapse text-left text-sm">
									<thead>
										<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
											<th className="w-8" />
											<th className="px-3 py-2">{t('catalog.filterTemplates.filterColumns.name')}</th>
											{TOGGLES.map(tg => (
												<th key={tg.field} className="px-3 py-2 text-center">
													{t(tg.labelKey)}
												</th>
											))}
											<th className="px-3 py-2" />
										</tr>
									</thead>
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
										<SortableContext items={rows.map(v => v.id!)} strategy={verticalListSortingStrategy}>
											<tbody>
												{rows.map(v => (
													<SortableValueRow key={v.id} id={v.id!} dragLabel={t('catalog.filterTemplates.dragToReorder')}>
														<td className="px-3 py-2">
															<div className="flex items-center gap-2">
																<span className="font-medium text-foreground">{v.FilterName || '—'}</span>
																{v.FilterTypeName && <Pill variant="neutral">{v.FilterTypeName}</Pill>}
															</div>
														</td>
														{TOGGLES.map(tg => (
															<td key={tg.field} className="px-3 py-2 text-center">
																<div className="flex justify-center">
																	<Switch
																		checked={Boolean(v[tg.field])}
																		disabled={update.isPending}
																		onCheckedChange={n => setFlag(v, tg.field, n)}
																		aria-label={t(tg.labelKey)}
																	/>
																</div>
															</td>
														))}
														<td className="px-3 py-2">
															<div className="flex justify-end">
																<Button
																	variant="ghost"
																	size="icon"
																	aria-label={t('catalog.filterTemplates.settings.title')}
																	title={t('catalog.filterTemplates.settings.title')}
																	onClick={() => setSettingsRow(v)}>
																	<Settings2 size={15} />
																</Button>
																<Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setDeleteRow(v)}>
																	<Trash2 size={15} className="text-danger-fg" />
																</Button>
															</div>
														</td>
													</SortableValueRow>
												))}
											</tbody>
										</SortableContext>
									</DndContext>
								</table>
							</div>
						)}
					</div>
				</SheetContent>
			</Sheet>

			<FilterValueSettingsDialog
				row={settingsRow}
				onOpenChange={o => !o && setSettingsRow(null)}
				onSave={token => update.mutateAsync(token)}
				saving={update.isPending}
			/>

			{templateId != null && <AddFiltersDialog open={addOpen} onOpenChange={setAddOpen} templateId={templateId} />}

			<Dialog open={Boolean(deleteRow)} onOpenChange={o => !o && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.filterTemplates.deleteFilterTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.filterTemplates.deleteFilterBody', { name: deleteRow?.FilterName || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
